import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useEffect, useState } from 'react'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { fetchDocumentPreviewBlob } from '../../query-api'

const mimeTypes: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
}

const getMimeTypeFromFileName = (fileName: string): string => {
  const fileExtension = fileName.split('.').pop()?.toLowerCase() ?? ''

  return mimeTypes[fileExtension] ?? 'application/octet-stream'
}

const ensureCorrectMimeType = (blob: Blob, fileName: string): Blob => {
  if (blob.type && blob.type !== 'application/octet-stream') {
    return blob
  }

  return new Blob([blob], { type: getMimeTypeFromFileName(fileName) })
}

const convertBlobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

export const useDocumentPreviewUrl = (organizationId: string, documentId: string, fileName: string) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!organizationId || !documentId) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    setIsLoading(true)
    setPreviewUrl(null)

    fetchDocumentPreviewBlob(organizationId, documentId)
      .then((blob) => convertBlobToDataUrl(ensureCorrectMimeType(blob, fileName)))
      .then((dataUrl) => {
        if (!cancelled) {
          setPreviewUrl(dataUrl)
          setIsLoading(false)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setIsLoading(false)
          trackError(err)
          notify({
            id: NotificationKeys.DocumentsPreviewFetch,
            message: err instanceof Error ? err.message : String(err),
            variant: 'error',
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [organizationId, documentId, fileName])

  return { previewUrl, isLoading }
}
